import { ActionIcon, Box, Center, Divider, Flex, FocusTrap, Text } from '@mantine/core';
import { BNButton } from '../../../components/Button/Button';
import { MobileModalProps } from '../../../components/MobilePriceUpdateForm/MobilePriceUpdateForm';
import { useNowPricing } from '../../NowPricing/NowPricing.hooks';
import { useForm } from '@mantine/form';
import { useViewportPosition } from '../../../utils/use-viewport-position';
import { useEffect, useRef, useState } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { useGlobalState } from '../../../data/Global.state';
import { BrokerNerdsBarkerCoreEnumsAdjustmentType, BrokerNerdsBarkerCoreModelsPricingSoftFloor, BrokerNerdsBarkerCoreModelsPricingTrailingFloor } from '../../../api';
import { BNNumberInput } from '../../../components/NumberInput/NumberInput';
import { BNSelect } from '../../../components/Select/Select';
import { formatCurrency } from '../../../utils/formatters';
import { getSoftFloorPeriodParts, getSoftFloorPeriodValue, type SoftFloorPeriod } from '../../../utils/soft-floor-period';
import { getTrailingFloorDisplayValue, getTrailingFloorRuleValue, isValidTrailingFloor, normalizeTrailingFloor } from '../../../utils/trailing-floor';
import mobileClasses from '../../../pages/ListingDetails.Mobile.module.css';
import DoNotDisturbIcon from '../../../components/icons/DoNotDisturb';
import AddCircleIcon from '../../../components/icons/AddCircle';

type FloorMode = 'standard' | 'trailing' | 'soft';
type FocusTarget = 'floor' | 'softFloor' | 'interval' | 'trailing';

const modalInputLabelProps = { fz: 'sm', fw: 500 };

const calculateFloorPriceFromTrailingFloor = (trailingFloor: BrokerNerdsBarkerCoreModelsPricingTrailingFloor | undefined, comparablePrice: number | null | undefined) => {
  if (!trailingFloor || comparablePrice == null) {
    return undefined;
  }

  const calculatedFloorPrice =
    trailingFloor.typeId === BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage ? comparablePrice * trailingFloor.value : comparablePrice + trailingFloor.value;

  return Math.max(0, parseFloat(calculatedFloorPrice.toFixed(2)));
};

const getPositiveAdjustmentDisplayValue = (value: number | undefined, type: BrokerNerdsBarkerCoreEnumsAdjustmentType) => {
  if (!value) {
    return 0;
  }

  if (type === BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage) {
    return parseFloat(((value - 1) * 100).toFixed(4));
  }

  return value;
};

const getPositiveAdjustmentRuleValue = (displayValue: number, type: BrokerNerdsBarkerCoreEnumsAdjustmentType) => {
  if (type === BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage) {
    return parseFloat((1 + displayValue / 100).toFixed(4));
  }

  return displayValue;
};

const getFocusTargetForFloorMode = (mode: FloorMode): FocusTarget => {
  if (mode === 'trailing') {
    return 'trailing';
  }

  if (mode === 'soft') {
    return 'softFloor';
  }

  return 'floor';
};

export function FloorUpdateForm({ closeModal }: MobileModalProps) {
  const { form, setAutoUpdateRuleOnTargetComparableChanges, targetComparablePrice } = useNowPricing('form', 'setAutoUpdateRuleOnTargetComparableChanges', 'targetComparablePrice');
  const hasSoftFloorFlag = useFlag('soft-floor-setting');
  const hasTrailingFloorFlag = useFlag('trailing-floor');
  const { principal } = useGlobalState('principal');

  const { values, setValues, setFieldValue, onSubmit } = useForm<{
    floorPrice: number | undefined;
    softFloor: BrokerNerdsBarkerCoreModelsPricingSoftFloor | undefined;
    trailingFloor: BrokerNerdsBarkerCoreModelsPricingTrailingFloor | undefined;
  }>({
    initialValues: {
      floorPrice: form.values.floorPrice ?? undefined,
      softFloor: form.values.softFloor ? { ...form.values.softFloor } : undefined,
      trailingFloor: form.values.trailingFloor ? { ...form.values.trailingFloor } : undefined,
    },
  });

  const floorInputRef = useRef<HTMLInputElement | null>(null);
  const softFloorInputRef = useRef<HTMLInputElement | null>(null);
  const intervalInputRef = useRef<HTMLInputElement | null>(null);
  const trailingInputRef = useRef<HTMLInputElement | null>(null);
  const [activeFocusTrap, setActiveFocusTrap] = useState<FocusTarget | null>(null);
  const [focusRequest, setFocusRequest] = useState<Record<FocusTarget, number>>({
    floor: 0,
    softFloor: 0,
    interval: 0,
    trailing: 0,
  });

  const hasSoftFloorFeatureEnabled = hasSoftFloorFlag && (principal?.settings?.pricerSettings?.featureFlags?.softFloor ?? false);
  const hasTrailingFloorFeatureEnabled = hasTrailingFloorFlag && (principal?.settings?.pricerSettings?.featureFlags?.trailingFloor ?? false);
  const hasSoftFloorOnRule = !!values.softFloor;
  const hasTrailingFloorOnRule = !!values.trailingFloor;
  const softFloorEnabled = (values.softFloor?.value ?? 0) > 0;
  const canUseSoftFloor = hasSoftFloorFeatureEnabled || hasSoftFloorOnRule;
  const canUseTrailingFloor = hasTrailingFloorFeatureEnabled || hasTrailingFloorOnRule;
  const showAdvancedFloorInput = canUseSoftFloor || canUseTrailingFloor;
  const floorMode: FloorMode = hasTrailingFloorOnRule ? 'trailing' : hasSoftFloorOnRule ? 'soft' : 'standard';

  const defaultSoftFloor =
    principal?.settings?.pricerSettings?.softFloor?.value && principal.settings.pricerSettings.softFloor.value > 0
      ? principal.settings.pricerSettings.softFloor
      : { period: '1.00:00:00', typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, value: 1.01 };

  const principalTrailingFloor = normalizeTrailingFloor(principal?.settings?.pricerSettings?.trailingFloor);
  const defaultTrailingFloor: BrokerNerdsBarkerCoreModelsPricingTrailingFloor =
    isValidTrailingFloor(principalTrailingFloor) && principalTrailingFloor
      ? { typeId: principalTrailingFloor.typeId, value: principalTrailingFloor.value }
      : { typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, value: -1 };

  const trailingFloorDisplayValue = getTrailingFloorDisplayValue(values.trailingFloor);
  const computedFloorPrice = floorMode === 'trailing' ? calculateFloorPriceFromTrailingFloor(values.trailingFloor, targetComparablePrice) : values.floorPrice;
  const softFloorType = values.softFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;
  const softFloorDisplayValue = getPositiveAdjustmentDisplayValue(values.softFloor?.value, softFloorType);
  const { unit: softFloorPeriodUnit, value: softFloorPeriodValue } = getSoftFloorPeriodParts(values.softFloor?.period);
  const normalizedSoftFloorPeriodValue = Math.max(1, softFloorPeriodValue ?? 1);
  const floorModeOptions = [
    { value: 'standard', label: 'Standard' },
    ...(canUseTrailingFloor ? [{ value: 'trailing', label: 'Trailing' }] : []),
    ...(canUseSoftFloor ? [{ value: 'soft', label: 'Soft' }] : []),
  ];
  const initialFocusTarget = getFocusTargetForFloorMode(floorMode);

  useViewportPosition(floorInputRef, [focusRequest.floor]);
  useViewportPosition(softFloorInputRef, [focusRequest.softFloor]);
  useViewportPosition(intervalInputRef, [focusRequest.interval]);
  useViewportPosition(trailingInputRef, [focusRequest.trailing]);

  const triggerInputFocus = (target: FocusTarget) => {
    setActiveFocusTrap(target);
    setFocusRequest((current) => ({ ...current, [target]: current[target] + 1 }));
  };

  useEffect(() => {
    setActiveFocusTrap(initialFocusTarget);
    setFocusRequest((current) => ({ ...current, [initialFocusTarget]: current[initialFocusTarget] + 1 }));
  }, [initialFocusTarget]);

  const clearInputTrap = (target: FocusTarget) => () => {
    if (activeFocusTrap === target) {
      setActiveFocusTrap(null);
    }
  };

  const setSoftFloorPeriod = (period: string) => {
    if (!values.softFloor) {
      return;
    }

    setFieldValue('softFloor', {
      ...values.softFloor,
      period,
    });
  };

  const setFloorMode = (mode: FloorMode) => {
    if (mode === 'standard') {
      setValues({
        ...values,
        softFloor: undefined,
        trailingFloor: undefined,
      });
      return;
    }

    if (mode === 'soft') {
      setValues({
        ...values,
        trailingFloor: undefined,
        softFloor: softFloorEnabled ? values.softFloor : { ...defaultSoftFloor },
      });
      return;
    }

    setValues({
      ...values,
      softFloor: undefined,
      trailingFloor: hasTrailingFloorOnRule && values.trailingFloor ? values.trailingFloor : { ...defaultTrailingFloor },
    });
  };

  return (
    <form
      onSubmit={onSubmit((nextValues) => {
        const nextFloorPrice = nextValues.trailingFloor ? calculateFloorPriceFromTrailingFloor(nextValues.trailingFloor, targetComparablePrice) : nextValues.floorPrice;

        setAutoUpdateRuleOnTargetComparableChanges(false);
        form.setValues({
          ...form.values,
          floorPrice: nextFloorPrice,
          softFloor: nextValues.softFloor,
          trailingFloor: nextValues.trailingFloor,
        });
        closeModal();
      })}
    >
      <Flex p={0} h="100%" justify="space-between">
        <Flex py="sm" px="md" gap="xs" align="center" justify="start" style={{ flex: 1 }}>
          <Text size="md" fw={600}>
            Floor
          </Text>
        </Flex>
      </Flex>
      <Divider color="var(--colors-gray-0)" />
      <Box py="xl" px="md">
        {showAdvancedFloorInput && (
          <BNSelect
            withinPortal
            label="Floor Type"
            labelProps={modalInputLabelProps}
            mb="md"
            value={floorMode}
            data={floorModeOptions}
            onChange={(value) => {
              if (value) {
                setFloorMode(value as FloorMode);
              }
            }}
          />
        )}
        {floorMode !== 'trailing' && (
          <FocusTrap active={activeFocusTrap === 'floor'}>
            <Flex w="100%" align="start" justify="center" gap="md">
              <ActionIcon
                className={mobileClasses.priceInputButton}
                onClick={() => {
                  triggerInputFocus('floor');
                  setFieldValue('floorPrice', (values.floorPrice ?? 0) - 1);
                }}
              >
                <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                  <DoNotDisturbIcon size={30} color="var(--colors-selectedBorder)" />
                </Center>
              </ActionIcon>
              <BNNumberInput
                ref={floorInputRef}
                value={values.floorPrice}
                onChange={(value) => {
                  setFieldValue('floorPrice', typeof value === 'string' ? undefined : value);
                }}
                onFocus={clearInputTrap('floor')}
                className={mobileClasses.priceInput}
                size="lg"
                decimalScale={2}
                fixedDecimalScale
                w="calc(100% - 100px)"
                hideControls
                leftSection={<Text fz="lg">$</Text>}
                data-autofocus={activeFocusTrap === 'floor' || undefined}
                selectOnFocus
                inputMode="decimal"
              />
              <ActionIcon
                className={mobileClasses.priceInputButton}
                onClick={() => {
                  triggerInputFocus('floor');
                  setFieldValue('floorPrice', (values.floorPrice ?? 0) + 1);
                }}
              >
                <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                  <AddCircleIcon size={30} color="var(--colors-selectedBorder)" />
                </Center>
              </ActionIcon>
            </Flex>
          </FocusTrap>
        )}
        {floorMode === 'trailing' && (
          <BNNumberInput
            ref={undefined}
            value={computedFloorPrice}
            onChange={(value) => {
              setFieldValue('floorPrice', typeof value === 'string' ? undefined : value);
            }}
            disabled
            size="lg"
            label="Floor"
            labelProps={modalInputLabelProps}
            decimalScale={2}
            fixedDecimalScale
            hideControls
            prefix="$"
            selectOnFocus
            inputMode="decimal"
          />
        )}
        {floorMode === 'soft' && values.softFloor && (
          <Box pt="md">
            <Text size="sm" fw={500} c="var(--colors-gray-5)" mb={4}>
              Soft Floor
            </Text>
            <FocusTrap active={activeFocusTrap === 'softFloor'}>
              <Flex w="100%" align="start" gap="sm">
                <BNSelect
                  withinPortal
                  size="lg"
                  w={88}
                  aria-label="Soft Floor Type"
                  value={softFloorType}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }

                    const nextType = value as BrokerNerdsBarkerCoreEnumsAdjustmentType;
                    setFieldValue('softFloor', {
                      ...values.softFloor,
                      typeId: nextType,
                      value: getPositiveAdjustmentRuleValue(softFloorDisplayValue, nextType),
                      period: values.softFloor?.period ?? '1.00:00:00',
                    });
                  }}
                  data={[
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, label: '$' },
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, label: '%' },
                  ]}
                />
                <Flex flex={1} align="start" justify="center" gap="md">
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      triggerInputFocus('softFloor');
                      setFieldValue('softFloor.value', getPositiveAdjustmentRuleValue(Math.max(0, softFloorDisplayValue - 1), softFloorType));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <DoNotDisturbIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                  <BNNumberInput
                    ref={softFloorInputRef}
                    value={softFloorDisplayValue}
                    onChange={(value) => {
                      const displayValue = typeof value === 'string' || value === undefined ? 0 : value;
                      setFieldValue('softFloor.value', getPositiveAdjustmentRuleValue(displayValue, softFloorType));
                    }}
                    onFocus={clearInputTrap('softFloor')}
                    error={form.errors['softFloor.value']}
                    errorPosition="bottom"
                    className={mobileClasses.priceInput}
                    size="lg"
                    decimalScale={2}
                    fixedDecimalScale
                    style={{ flex: 1 }}
                    hideControls
                    leftSection={<Text fz="lg">{softFloorType === BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount ? '$' : '%'}</Text>}
                    data-autofocus={activeFocusTrap === 'softFloor' || undefined}
                    selectOnFocus
                    inputMode="decimal"
                    min={0}
                  />
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      triggerInputFocus('softFloor');
                      setFieldValue('softFloor.value', getPositiveAdjustmentRuleValue(softFloorDisplayValue + 1, softFloorType));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <AddCircleIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                </Flex>
              </Flex>
            </FocusTrap>
            <Text size="sm" fw={500} c="var(--colors-gray-5)" mt="md" mb={4}>
              Interval
            </Text>
            <FocusTrap active={activeFocusTrap === 'interval'}>
              <Flex w="100%" align="start" gap="sm">
                <BNSelect
                  withinPortal
                  size="lg"
                  w={88}
                  aria-label="Interval Unit"
                  value={softFloorPeriodUnit}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }

                    setSoftFloorPeriod(getSoftFloorPeriodValue(normalizedSoftFloorPeriodValue, value as SoftFloorPeriod));
                  }}
                  data={['Min', 'Hr', 'Day']}
                />
                <Flex flex={1} align="start" justify="center" gap="md">
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      triggerInputFocus('interval');
                      setSoftFloorPeriod(getSoftFloorPeriodValue(Math.max(1, normalizedSoftFloorPeriodValue - 1), softFloorPeriodUnit));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <DoNotDisturbIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                  <BNNumberInput
                    ref={intervalInputRef}
                    value={softFloorPeriodValue}
                    onChange={(value) => {
                      const nextValue = typeof value === 'string' || value === undefined ? 0 : value;
                      setSoftFloorPeriod(getSoftFloorPeriodValue(nextValue, softFloorPeriodUnit));
                    }}
                    onFocus={clearInputTrap('interval')}
                    error={form.errors['softFloor.period']}
                    errorPosition="bottom"
                    className={mobileClasses.priceInput}
                    size="lg"
                    decimalScale={0}
                    fixedDecimalScale
                    style={{ flex: 1 }}
                    hideControls
                    data-autofocus={activeFocusTrap === 'interval' || undefined}
                    selectOnFocus
                    inputMode="numeric"
                    min={1}
                    step={1}
                  />
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      triggerInputFocus('interval');
                      setSoftFloorPeriod(getSoftFloorPeriodValue(normalizedSoftFloorPeriodValue + 1, softFloorPeriodUnit));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <AddCircleIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                </Flex>
              </Flex>
            </FocusTrap>
          </Box>
        )}
        {floorMode === 'trailing' && values.trailingFloor && (
          <Box pt="md">
            <Text size="sm" fw={500} c="var(--colors-gray-5)" mb={4}>
              Trailing Floor
            </Text>
            <FocusTrap active={activeFocusTrap === 'trailing'}>
              <Flex w="100%" align="start" gap="sm">
                <BNSelect
                  withinPortal
                  size="lg"
                  w={88}
                  aria-label="Trailing Floor Type"
                  value={values.trailingFloor.typeId}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }

                    const nextType = value as BrokerNerdsBarkerCoreEnumsAdjustmentType;
                    setFieldValue('trailingFloor', {
                      typeId: nextType,
                      value: getTrailingFloorRuleValue(trailingFloorDisplayValue, nextType),
                    });
                  }}
                  data={[
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, label: '$' },
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, label: '%' },
                  ]}
                />
                <Flex flex={1} align="start" justify="center" gap="md">
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      const typeId = values.trailingFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;
                      triggerInputFocus('trailing');
                      setFieldValue('trailingFloor.value', getTrailingFloorRuleValue(trailingFloorDisplayValue - 1, typeId));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <DoNotDisturbIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                  <BNNumberInput
                    ref={trailingInputRef}
                    size="lg"
                    value={trailingFloorDisplayValue}
                    onChange={(value) => {
                      const displayValue = typeof value === 'string' || value === undefined ? 0 : value;
                      const typeId = values.trailingFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;

                      setFieldValue('trailingFloor.value', getTrailingFloorRuleValue(displayValue, typeId));
                    }}
                    onFocus={clearInputTrap('trailing')}
                    error={form.errors['trailingFloor.value']}
                    errorPosition="bottom"
                    className={mobileClasses.priceInput}
                    style={{ flex: 1 }}
                    allowNegative
                    hideControls
                    decimalScale={2}
                    fixedDecimalScale
                    max={0}
                    data-autofocus={activeFocusTrap === 'trailing' || undefined}
                    selectOnFocus
                    inputMode="decimal"
                  />
                  <ActionIcon
                    className={mobileClasses.priceInputButton}
                    onClick={() => {
                      const typeId = values.trailingFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;
                      triggerInputFocus('trailing');
                      setFieldValue('trailingFloor.value', getTrailingFloorRuleValue(Math.min(0, trailingFloorDisplayValue + 1), typeId));
                    }}
                  >
                    <Center h={36} w={36} bg="var(--colors-gray-0)" style={{ borderRadius: 100 }}>
                      <AddCircleIcon size={30} color="var(--colors-selectedBorder)" />
                    </Center>
                  </ActionIcon>
                </Flex>
              </Flex>
            </FocusTrap>
            <Flex pt="sm" align="center" justify="space-between" gap="sm">
              <Text size="xs" c="var(--colors-gray-5)">
                Target Comparable
              </Text>
              <Text size="sm" fw={600}>
                {targetComparablePrice == null ? '--' : formatCurrency(targetComparablePrice)}
              </Text>
            </Flex>
          </Box>
        )}
      </Box>
      <Divider color="var(--colors-gray-0)" />
      <Flex gap="sm" w="100%" py="xl" px="md">
        <BNButton fullWidth variant="default" onClick={() => closeModal()}>
          Cancel
        </BNButton>
        <BNButton fullWidth variant="filled" color="gray" type="submit">
          Done
        </BNButton>
      </Flex>
    </form>
  );
}
